// ----------------------------------------------------------------------- react
import { useCallback, useEffect, useRef, useState } from "react";

// ------------------------------------------------------------------- internos
import {
  hojaDeSalidas, choferesDeLaPlanilla, filasDeSalidas, cualesYaEstan, agregarASalidas,
  aConfirmar, notaDeTrafico,
} from "../../shared/salidasBitren";
import {
  hojaDeSalidasFurgones, choferesDeFurgones, filasDeComunes, cualesYaEstanFurgones,
  agregarASalidasFurgones,
} from "../../shared/salidasFurgones";
import { pelado, textoSalteadas } from "../../shared/torrecillas";
import { traerSalidas, traerSalidasDelExcel, cargarEnganchesDeSalidas } from "../../services/salidasBitren";
import { bitrenesParaEnganches, textoEnganches } from "../../shared/salidasEnganches";
import { retener, PORQUE_NO_SE_PUDO_LEER } from "../../services/archivoRetenido";
import { traerPlanilla, guardarPlanilla, guardarVersion, CONFLICTO } from "../../services/espacio";
import { ponerUbicacionAlAgregar, textoUbicacionAlAgregar } from "../../services/ubicacionAlAgregar";

/* Las cargas de Buenos Aires → SALIDAS BITREN de su ONLY BITRENES y SALIDAS de
 * su planilla de furgones.
 *
 * Ticket #432 (Lautaro Díaz, Satelital, 16/10): «LO MISMO SERIA CON LAS
 * CARGAS DIARIAS DE GULA, QUE SON LAS TORRECILLAS DE BUENOS AIRES». Va en la
 * pantalla de Torrecillas, abajo: lo de arriba es lo que llega de Río Grande
 * (a ARRIBOS BITREN), y esto es el otro sentido.
 *
 * Los playos 25xx van igual que los semis 15xx, en pareja (Lautaro, 19/10:
 * «SISI LOS BITRENES 1000 Y LOS 2000 VAN A SALIDAS BITREN.»): FlotaBot los
 * trae en `bitrenes`, y el que no tiene a su par, en `sueltos`.
 *
 * DOS FUENTES (Lautaro, 20/10). «Ver las salidas del día» las trae de la
 * Previsión, que Tráfico carga a mano y a la hora que puede: el 20/10 a las
 * 9:51 no tenía ninguna salida y esta pantalla dijo «no sale ningún bitren»,
 * que era falso. Ahora, si la Previsión no tiene ninguna salida ese día, lo
 * dice («Tráfico todavía no cargó…»). Y él puede soltar el Excel de Gula, como
 * las Torrecillas: «se me ocurre que en vez de hacer asi podriamos hacer como
 * con torrecillas, que te arrastre el excel y se complete en salidas
 * bitrenes y en salidas y cortes». FlotaBot lee ese día del Excel y contesta
 * lo mismo que con la Previsión; no guarda nada.
 *
 * LOS FURGONES COMUNES (Lautaro, 20/10: «La de only bitrenes es solo para los
 * bitrenes y arribos y cortes solo para furgones normales»): abajo de los
 * bitrenes, para la hoja SALIDAS de la planilla de furgones que él elige
 * (la pantalla se acuerda, por persona, en este navegador). Las reglas, en
 * `shared/salidasFurgones`.
 *
 * CADA FURGÓN TIENE SU CASILLA (revisión del 20/10). Antes «Agregar N
 * furgones» escribía todos los nuevos y no había cómo sacar uno: el 15/10 le
 * habría escrito «692 · PISO LISO C/BARRALES HABILITADO», un pedido de
 * Tráfico. Ahora FlotaBot manda aparte lo que todavía no es una salida (sin
 * tractor: «Sin tractor todavía»), lo «a confirmar» viene con TR y CHOFER
 * vacíos como en los bitrenes, y cada renglón se puede destildar: el
 * destildado no se escribe y queda ahí, nuevo, por si después va. Arrancan
 * tildados los confirmados; lo «a confirmar» arranca SIN tildar (Lautaro,
 * 20/10: «NOno, que aparezca sin nada»): lo tilda él si va.
 *
 * Primero se ven, con cuáles ya están en sus hojas, y recién con los botones
 * se escriben; nada se pisa y dos veces no duplica. Antes de escribir se saca
 * una foto al Historial; si no se puede, no se escribe nada. Nada queda
 * debajo de sus celdas combinadas: si para eso algo baja y saltea filas, el
 * aviso dice cuáles (`textoSalteadas`). Sin textos de ayuda en la pantalla,
 * como el resto de Torrecillas.
 *
 * LO QUE SE MUESTRA ES DE UN DÍA Y UNAS PLANILLAS. Mientras busca o guarda, el
 * día y las planillas no se cambian; la respuesta trae su `fecha` y se guarda
 * con ella, y la que no es del día elegido se descarta: los bitrenes del 16
 * nunca se escriben con F/C 17.
 *
 * El F/S va con el mismo día que el F/C (Lautaro, 19/10, ticket #445: «LO QUE
 * CARGA EN BUE HOY TAMBIÉN SALE HOY»), en los bitrenes y en los furgones.
 *
 * Y LOS BITRENES AGREGADOS VAN TAMBIÉN A ENGANCHES (Lautaro, 21/10), hacia
 * Río Grande con la 2da parte para completar: `shared/salidasEnganches`.
 */

const hoyISO = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

const plural = (n, uno, varios) => `${n} ${n === 1 ? uno : varios}`;

/* La planilla de furgones que eligió cada persona, en este navegador. Sin
   almacenamiento (modo privado, bloqueado) se propone la de siempre. */
const MEMORIA = "torrecillas.planillaFurgones";

function planillaRecordada(yo) {
  try {
    return (JSON.parse(localStorage.getItem(MEMORIA) || "{}") || {})[yo] || "";
  } catch {
    return "";
  }
}

function recordarPlanilla(yo, id) {
  try {
    const todas = JSON.parse(localStorage.getItem(MEMORIA) || "{}") || {};
    localStorage.setItem(MEMORIA, JSON.stringify({ ...todas, [yo]: id }));
  } catch {
    /* sin almacenamiento: la próxima vez se propone la de siempre */
  }
}

/* La que se propone: la recordada si sigue siendo suya; si no, la que se
   llama ARRIBOS o CORTES (su ARRIBOS Y CORTES); si no hay, la primera que no
   es de bitrenes. Por el nombre y no por la última tocada: la lista viene
   por `actualizado_en`, y él tiene seis que no son de bitrenes (REVISIONES
   2026, INTERNOS, CONTACTOS GENERALES…): la primera de ésas es la última que
   guardó, no la de furgones. */
function planillaPropuesta(planillas, yo) {
  const recordada = planillaRecordada(yo);
  if (recordada && planillas.some((p) => p.id === recordada)) return recordada;
  const deFurgones = (p) => !pelado(p.nombre).includes("BITREN");
  const porNombre = planillas.find((p) => deFurgones(p) && /ARRIBOS|CORTES/.test(pelado(p.nombre)));
  return (porNombre || planillas.find(deFurgones) || planillas[0] || {}).id || "";
}

/* Por qué un bitren va sin tractor: lo que dice cada fila de la Previsión. */
const enLaPrevision = (b) =>
  b.tractor || !b.previsto?.length
    ? ""
    : b.previsto
      .map((p) => [p.acoplado, p.tractor, p.chofer, notaDeTrafico(p.nota)].filter(Boolean).join(" · "))
      .join(" / ");

const txt = (v) => String(v ?? "").replace(/\s+/g, " ").trim();

/* Lo que anotó Tráfico de un furgón común, si no es lo que se va a escribir:
   el furgón con su «(V)», la patente armada, el chofer con nombre completo,
   o el tractor y el chofer de lo que viene «a confirmar». */
const loQueAnoto = (c) => {
  const p = c.previsto;
  if (!p) return "";
  const anotado = txt(p.acoplado).toUpperCase() !== txt(c.furgon).toUpperCase() ? txt(p.acoplado) : "";
  const dijo = [anotado, p.tractor, p.chofer, notaDeTrafico(p.nota)].filter(Boolean).join(" · ");
  if (!dijo) return "";
  return anotado || dijo !== [c.tractor, c.chofer].filter(Boolean).join(" · ") ? dijo : "";
};


export default function SalidasBuenosAires({
  planillas = [], planillaId = "", onPlanilla = () => {}, planillasFurgones = [], yo = "",
  onExcelDeGula = () => {},
}) {
  const entrada = useRef(null);
  const [encima, setEncima] = useState(false);
  const [fecha, setFecha] = useState(hoyISO);
  const [furgonesId, setFurgonesId] = useState("");
  const [filas, setFilas] = useState(null);
  const [vista, setVista] = useState(null); // { fecha, planillaId, furgonesId, fuente, hoja, archivo }
  const [vacia, setVacia] = useState(false);
  const [sueltos, setSueltos] = useState([]);
  const [yaEstan, setYaEstan] = useState(new Set());
  const [sinHoja, setSinHoja] = useState(false);
  const [cambios, setCambios] = useState({});
  const [comunes, setComunes] = useState([]);
  const [sinFurgon, setSinFurgon] = useState([]);
  const [sinTractor, setSinTractor] = useState([]);
  const [destildados, setDestildados] = useState(new Set()); // ids de los comunes que no van
  const [yaEstanC, setYaEstanC] = useState(new Set());
  const [sinHojaC, setSinHojaC] = useState(false);
  const [cambiosC, setCambiosC] = useState({});
  const [ocupado, setOcupado] = useState("");
  const [error, setError] = useState("");
  const [aviso, setAviso] = useState("");
  const [avisoUbic, setAvisoUbic] = useState("");
  const [avisoEng, setAvisoEng] = useState("");
  // Qué «Agregar» es el último: la ubicación de uno viejo no se muestra.
  const vuelta = useRef(0);
  const [avisoC, setAvisoC] = useState("");

  // Lo elegido ahora, para saber al volver una respuesta si sigue siendo eso.
  const elegido = useRef({ fecha, planillaId, furgonesId });
  elegido.current = { fecha, planillaId, furgonesId };

  const conCambios = (filas || []).map((f) => ({ ...f, ...(cambios[f.primera] || {}) }));
  const nuevos = conCambios.filter((f) => !yaEstan.has(f.primera));
  const comunesConCambios = comunes.map((c) => ({ ...c, ...(cambiosC[c.id] || {}) }));
  const nuevosC = comunesConCambios.filter((c) => !yaEstanC.has(c.furgon));
  const elegidosC = nuevosC.filter((c) => !destildados.has(c.id));

  const limpiar = useCallback(() => {
    setFilas(null);
    setVista(null);
    setVacia(false);
    setSueltos([]);
    setYaEstan(new Set());
    setSinHoja(false);
    setCambios({});
    setComunes([]);
    setSinFurgon([]);
    setSinTractor([]);
    setDestildados(new Set());
    setYaEstanC(new Set());
    setSinHojaC(false);
    setCambiosC({});
    setError("");
    setAviso("");
    setAvisoUbic("");
    setAvisoEng("");
    vuelta.current += 1;
    setAvisoC("");
  }, []);

  // La planilla de bitrenes también se elige arriba, en la parte de Río Grande.
  useEffect(() => {
    limpiar();
  }, [planillaId, limpiar]);

  // La de furgones: la que eligió la última vez, si sigue estando.
  useEffect(() => {
    setFurgonesId((actual) => (planillasFurgones.some((p) => p.id === actual)
      ? actual
      : planillaPropuesta(planillasFurgones, yo)));
  }, [planillasFurgones, yo]);

  /* Una planilla, para saber qué ya está y cómo escribe él los choferes. Sin
     planilla igual se ven las salidas. */
  const leerPlanilla = async (id) => {
    if (!id) return null;
    try {
      return await traerPlanilla(id);
    } catch {
      return null;
    }
  };

  /* Lo que contestó FlotaBot (de la Previsión o del Excel), a la pantalla. */
  const mostrar = async (pedido, traer) => {
    limpiar();
    setOcupado(pedido.fuente);
    const r = await traer();
    // La de furgones se lee sólo si hay comunes: sin ellos no hace falta.
    const hayComunes = r?.ok && (r.comunes || []).length > 0;
    const [p, pf] = await Promise.all([
      leerPlanilla(pedido.planillaId),
      hayComunes ? leerPlanilla(pedido.furgonesId) : Promise.resolve(null),
    ]);
    setOcupado("");
    // Si mientras tanto cambió lo elegido, esto ya no es lo que él mira.
    const ahora = elegido.current;
    if (ahora.fecha !== pedido.fecha || ahora.planillaId !== pedido.planillaId
      || ahora.furgonesId !== pedido.furgonesId) return;
    if (!r.ok) {
      setError(r.mensaje || r.error || "No se pudo consultar.");
      return;
    }
    if (r.fecha !== pedido.fecha) {
      setError("No se pudo consultar. Probá de nuevo.");
      return;
    }
    // Lo leído del Excel de Gula, también para el correo del Car-Truck de
    // Buenos Aires (Lautaro, 27/10, ticket 536): sin volver a mandarlo.
    if (pedido.fuente === "excel") {
      onExcelDeGula({ fecha: r.fecha, archivo: pedido.archivo, bitrenes: r.bitrenes || [], comunes: r.comunes || [] });
    }
    const i = p ? hojaDeSalidas(p.datos) : -1;
    const f = filasDeSalidas(r.bitrenes, p ? choferesDeLaPlanilla(p.datos) : {});
    setFilas(f);
    setVista({ ...pedido, fecha: r.fecha, hoja: r.hoja || "" });
    setVacia(Boolean(r.prevision_vacia));
    setSueltos(r.sueltos || []);
    setSinHoja(Boolean(p) && i < 0);
    setYaEstan(i >= 0 ? cualesYaEstan(p.datos.hojas[i], f, r.fecha) : new Set());
    const iC = pf ? hojaDeSalidasFurgones(pf.datos) : -1;
    const c = filasDeComunes(r.comunes, pf ? choferesDeFurgones(pf.datos) : {});
    setComunes(c);
    // Lo «a confirmar» arranca destildado: lo tilda él si va.
    setDestildados(new Set(c.filter((x) => !x.confirmado).map((x) => x.id)));
    setSinFurgon(r.sin_furgon || []);
    setSinTractor(r.sin_tractor || []);
    setSinHojaC(Boolean(pf) && iC < 0);
    setYaEstanC(iC >= 0 ? cualesYaEstanFurgones(pf.datos.hojas[iC], c) : new Set());
  };

  const ver = () => {
    const pedido = { fecha, planillaId, furgonesId, fuente: "prevision" };
    return mostrar(pedido, () => traerSalidas(pedido.fecha));
  };

  /* El Excel de Gula. Los bytes se leen al soltarlo (`archivoRetenido`): un
     `File` es un puntero al disco, y entre soltar y que conteste el Excel
     puede abrirse o guardarse. */
  const tomar = async (archivo) => {
    if (!archivo) return;
    const pedido = { fecha, planillaId, furgonesId, fuente: "excel", archivo: archivo.name };
    let copia;
    try {
      copia = await retener(archivo);
    } catch {
      limpiar();
      setError(PORQUE_NO_SE_PUDO_LEER);
      return;
    }
    await mostrar(pedido, () => traerSalidasDelExcel(pedido.fecha, copia));
  };

  const cambiar = (primera, campo, valor) =>
    setCambios((c) => ({ ...c, [primera]: { ...(c[primera] || {}), [campo]: valor } }));

  const cambiarC = (id, campo, valor) =>
    setCambiosC((c) => ({ ...c, [id]: { ...(c[id] || {}), [campo]: valor } }));

  const tildar = (id) =>
    setDestildados((d) => {
      const otro = new Set(d);
      if (otro.has(id)) otro.delete(id);
      else otro.add(id);
      return otro;
    });

  /* La posición de ahora en la UBICACIÓN de los bitrenes recién agregados
     (Lautaro, 21/10: «Al agregar a la planilla las Torrecillas o las cargas
     diarias de Gula, que también ponga la posición actual»). Va después de
     guardar y sin esperarla: los bitrenes ya entraron, y si FlotaBot tarda o
     no contesta la UBICACIÓN queda vacía, como antes. */
  const ubicar = async (enLa, hoja, nuevas, mia) => {
    const r = await ponerUbicacionAlAgregar({ planillaId: enLa, hojaDe: hojaDeSalidas, hoja, nuevas });
    if (vuelta.current === mia) setAvisoUbic(textoUbicacionAlAgregar(r));
  };

  /* Los bitrenes del día, también a Enganches hacia Río Grande (Lautaro,
     21/10: «cuando se cargue en salidas de only bitrenes lo que hay en
     cargas diarias, tambien se cargue en enganches, para que yo solo tenga
     que cargar el chofer que saca la 2da parte»). Como la UBICACIÓN:
     después de guardar y sin esperarlo; FlotaBot no duplica lo que ya está,
     y si no contesta la planilla quedó igual y se dice. Van los agregados Y
     los que ya estaban en SALIDAS BITREN (revisión del 21/10): uno escrito a
     mano, o agregado antes de que esto existiera, si no nunca llegaba. */
  const enganchar = async (bitrenes, delDia, mia) => {
    const van = bitrenesParaEnganches(bitrenes, delDia);
    if (!van.length) return;
    let r;
    try {
      r = await cargarEnganchesDeSalidas(van);
    } catch {
      r = { ok: false };
    }
    if (vuelta.current === mia) setAvisoEng(textoEnganches(r));
  };

  const agregar = async () => {
    if (!vista) return;
    const { fecha: delDia, planillaId: enLa } = vista;
    vuelta.current += 1;
    const mia = vuelta.current;
    setOcupado("agregar");
    setError("");
    setAviso("");
    setAvisoUbic("");
    setAvisoEng("");
    try {
      // Se vuelve a traer justo antes: lo que él escribió recién no se pisa.
      const p = await traerPlanilla(enLa);
      const i = hojaDeSalidas(p.datos);
      if (i < 0) {
        setSinHoja(true);
        return;
      }
      const r = agregarASalidas(p.datos.hojas[i], conCambios, delDia);
      if (r.agregados) {
        // Primero la foto de cómo estaba, al Historial: sin ella no se escribe.
        try {
          await guardarVersion(p.id);
        } catch {
          setError("No se pudo agregar. No se escribió nada; probá de nuevo.");
          return;
        }
        const hojas = p.datos.hojas.map((h, j) => (j === i ? r.hoja : h));
        await guardarPlanilla(p.id, p.nombre, { ...p.datos, hojas }, p.actualizado_en);
      }
      setYaEstan(cualesYaEstan(r.hoja, conCambios, delDia));
      setAviso(
        `${p.nombre}: ${plural(r.agregados, "bitren agregado", "bitrenes agregados")}`
        + (r.yaEstaban ? `, ${r.yaEstaban} ya ${r.yaEstaban === 1 ? "estaba" : "estaban"}.` : ".")
        + (r.salteadas?.length ? ` ${textoSalteadas(r.salteadas)}` : ""),
      );
      if (r.agregados) ubicar(p.id, r.hoja, r.nuevas, mia);
      enganchar([...(r.bitrenes || []), ...(r.estaban || [])], delDia, mia);
    } catch (e) {
      setError(
        e?.codigo === CONFLICTO
          ? "La planilla cambió mientras tanto. Probá de nuevo."
          : "No se pudo guardar la planilla. Probá de nuevo.",
      );
    } finally {
      setOcupado("");
    }
  };

  /* Si ya estaban todos en la planilla no hay nada que agregar, pero sí
     pueden faltar en Enganches: este botón los manda sin tocar la planilla. */
  const pasarAEnganches = async () => {
    if (!vista) return;
    vuelta.current += 1;
    const mia = vuelta.current;
    setOcupado("enganches");
    setAvisoEng("");
    try {
      await enganchar(conCambios, vista.fecha, mia);
    } finally {
      setOcupado("");
    }
  };

  /* Los furgones comunes a la SALIDAS de su planilla de furgones: la misma
     mecánica que los bitrenes. Van los tildados; los que ya estaban se pasan
     igual, para que el aviso diga cuántos ya estaban. */
  const agregarFurgones = async () => {
    if (!vista) return;
    setOcupado("agregarFurgones");
    setError("");
    setAvisoC("");
    try {
      const p = await traerPlanilla(vista.furgonesId);
      const i = hojaDeSalidasFurgones(p.datos);
      if (i < 0) {
        setSinHojaC(true);
        return;
      }
      const van = comunesConCambios.filter((c) => yaEstanC.has(c.furgon) || !destildados.has(c.id));
      const r = agregarASalidasFurgones(p.datos.hojas[i], van);
      if (r.agregados) {
        try {
          await guardarVersion(p.id);
        } catch {
          setError("No se pudo agregar. No se escribió nada; probá de nuevo.");
          return;
        }
        const hojas = p.datos.hojas.map((h, j) => (j === i ? r.hoja : h));
        await guardarPlanilla(p.id, p.nombre, { ...p.datos, hojas }, p.actualizado_en);
      }
      setYaEstanC(cualesYaEstanFurgones(r.hoja, comunesConCambios));
      setAvisoC(
        `${p.nombre}: ${plural(r.agregados, "furgón agregado", "furgones agregados")}`
        + (r.yaEstaban ? `, ${r.yaEstaban} ya ${r.yaEstaban === 1 ? "estaba" : "estaban"}.` : ".")
        + (r.salteadas?.length ? ` ${textoSalteadas(r.salteadas, "furgón")}` : ""),
      );
    } catch (e) {
      setError(
        e?.codigo === CONFLICTO
          ? "La planilla cambió mientras tanto. Probá de nuevo."
          : "No se pudo guardar la planilla. Probá de nuevo.",
      );
    } finally {
      setOcupado("");
    }
  };

  return (
    <section style={{ marginTop: 24 }}>
      <h3 style={{ margin: "0 0 8px" }}>Salidas de Buenos Aires</h3>

      <p style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
        <label>
          Día de carga{" "}
          <input
            type="date"
            value={fecha}
            disabled={!!ocupado}
            onChange={(e) => {
              setFecha(e.target.value);
              limpiar();
            }}
          />
        </label>
        {planillas.length > 0 && (
          <select
            value={planillaId}
            disabled={!!ocupado}
            onChange={(e) => {
              onPlanilla(e.target.value);
              limpiar();
            }}
            aria-label="Planilla de bitrenes de las salidas"
          >
            {planillas.map((p) => (
              <option key={p.id} value={p.id}>{p.nombre}</option>
            ))}
          </select>
        )}
        {planillasFurgones.length > 0 && (
          <select
            value={furgonesId}
            disabled={!!ocupado}
            onChange={(e) => {
              setFurgonesId(e.target.value);
              recordarPlanilla(yo, e.target.value);
              limpiar();
            }}
            aria-label="Planilla de furgones de las salidas"
          >
            {planillasFurgones.map((p) => (
              <option key={p.id} value={p.id}>{p.nombre}</option>
            ))}
          </select>
        )}
        <button type="button" className="tk-boton" disabled={!fecha || !!ocupado} onClick={ver}>
          {ocupado === "prevision" ? "Buscando…" : "Ver las salidas del día"}
        </button>
      </p>

      {vacia && (
        <div className="panel-aviso suave">
          <p>Tráfico todavía no cargó la Previsión de ese día. Soltá acá el Excel de Gula.</p>
        </div>
      )}

      <div
        className={"planilla-cajon" + (encima ? " planilla-cajon--encima" : "")}
        onDragOver={(e) => {
          e.preventDefault();
          setEncima(true);
        }}
        onDragLeave={() => setEncima(false)}
        onDrop={(e) => {
          e.preventDefault();
          setEncima(false);
          if (!ocupado && fecha) tomar(e.dataTransfer.files?.[0]);
        }}
      >
        <p className="planilla-cajon__txt">
          {ocupado === "excel" ? "Leyendo el Excel…" : "Soltá acá el Excel de Gula"}
          <br />
          <span className="tk-meta">o</span>
        </p>
        <button
          type="button"
          className="tk-boton"
          disabled={!fecha || !!ocupado}
          onClick={() => entrada.current?.click()}
        >
          Buscar en el equipo
        </button>
        <input
          ref={entrada}
          type="file"
          accept=".xlsx,.xlsm"
          hidden
          aria-label="Excel de Gula"
          onChange={(e) => {
            tomar(e.target.files?.[0]);
            e.target.value = "";
          }}
        />
        {vista?.fuente === "excel" && (
          <p className="tk-meta">{vista.archivo} · {vista.hoja}</p>
        )}
        {/* «Limpiar» (Lautaro, 29/10, ticket 566): saca de la pantalla lo
            leído, para soltar otro Excel sin refrescar la página. */}
        {(vista || error) && (
          <button type="button" className="tk-boton" disabled={!!ocupado} onClick={limpiar}>
            Limpiar
          </button>
        )}
      </div>

      {error && (
        <div className="panel-aviso suave">
          <p>{error}</p>
        </div>
      )}

      {filas && !filas.length && !sueltos.length && !vacia && (
        <p>Ese día no sale ningún bitren de Buenos Aires.</p>
      )}

      {filas && filas.length > 0 && (
        <div style={{ overflowX: "auto" }}>
          <table className="tabla fija">
            <thead>
              <tr>
                <th>FUR</th>
                <th>TRAC</th>
                <th>Chofer</th>
                <th>Cliente</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {conCambios.map((f) => {
                const esta = yaEstan.has(f.primera);
                return (
                  <tr key={f.primera}>
                    <td>{f.primera} / {f.segunda}</td>
                    <td>
                      {f.tractor || (aConfirmar(f) && <b>a confirmar</b>)}
                      {enLaPrevision(f) && <div className="tk-meta">{enLaPrevision(f)}</div>}
                    </td>
                    <td>
                      <input
                        type="text"
                        value={f.chofer}
                        disabled={esta}
                        aria-label={`Chofer de ${f.primera}`}
                        onChange={(e) => cambiar(f.primera, "chofer", e.target.value.toUpperCase())}
                      />
                    </td>
                    <td>
                      <input
                        type="text"
                        value={f.cliente}
                        disabled={esta}
                        aria-label={`Cliente de ${f.primera}`}
                        onChange={(e) => cambiar(f.primera, "cliente", e.target.value.toUpperCase())}
                      />
                    </td>
                    <td>{esta ? "Ya está" : "Nuevo"}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {sueltos.length > 0 && (
        <p className="tk-meta">
          Sin su par ese día:{" "}
          {sueltos.map((s) => [s.semi, s.tractor, s.chofer].filter(Boolean).join(" · ")).join(" / ")}
        </p>
      )}

      {sinHoja && (
        <div className="panel-aviso suave">
          <p>Esa planilla no tiene la hoja de salidas.</p>
        </div>
      )}

      {filas && filas.length > 0 && planillaId && !sinHoja && (
        <p>
          {nuevos.length > 0 ? (
            <button type="button" className="tk-boton tk-boton--fuerte" disabled={!!ocupado} onClick={agregar}>
              {ocupado === "agregar"
                ? "Guardando…"
                : `Agregar ${plural(nuevos.length, "bitren", "bitrenes")} a SALIDAS BITREN`}
            </button>
          ) : (
            !aviso && (
              <>
                {"Ya están todos en la planilla. "}
                <button type="button" className="tk-boton" disabled={!!ocupado} onClick={pasarAEnganches}>
                  {ocupado === "enganches"
                    ? "Pasando…"
                    : `Pasar ${plural(conCambios.length, "bitren", "bitrenes")} a Enganches`}
                </button>
              </>
            )
          )}
        </p>
      )}

      {!aviso && avisoEng && (
        <div className="panel-aviso suave">
          <p>{avisoEng}</p>
        </div>
      )}

      {aviso && (
        <div className="panel-aviso suave">
          <p>{aviso}</p>
          {avisoUbic && <p>{avisoUbic}</p>}
          {avisoEng && <p>{avisoEng}</p>}
        </div>
      )}

      {comunes.length > 0 && (
        <>
          <h4 style={{ margin: "16px 0 8px" }}>Furgones</h4>
          <div style={{ overflowX: "auto" }}>
            <table className="tabla fija">
              <thead>
                <tr>
                  <th />
                  <th>FR</th>
                  <th>TR</th>
                  <th>Chofer</th>
                  <th>Cliente</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {comunesConCambios.map((c) => {
                  const esta = yaEstanC.has(c.furgon);
                  return (
                    <tr key={c.id}>
                      <td>
                        <input
                          type="checkbox"
                          checked={!esta && !destildados.has(c.id)}
                          disabled={esta || !!ocupado}
                          aria-label={`Agregar el ${c.furgon}`}
                          onChange={() => tildar(c.id)}
                        />
                      </td>
                      <td>{c.furgon}</td>
                      <td>
                        <input
                          type="text"
                          value={c.tractor}
                          disabled={esta}
                          size={9}
                          aria-label={`Tractor de ${c.furgon}`}
                          onChange={(e) => cambiarC(c.id, "tractor", e.target.value.toUpperCase())}
                        />
                      </td>
                      <td>
                        <input
                          type="text"
                          value={c.chofer}
                          disabled={esta}
                          aria-label={`Chofer de ${c.furgon}`}
                          onChange={(e) => cambiarC(c.id, "chofer", e.target.value.toUpperCase())}
                        />
                        {!c.confirmado && <div><b>a confirmar</b></div>}
                        {loQueAnoto(c) && <div className="tk-meta">{loQueAnoto(c)}</div>}
                      </td>
                      <td>
                        <input
                          type="text"
                          value={c.cliente}
                          disabled={esta}
                          aria-label={`Cliente de ${c.furgon}`}
                          onChange={(e) => cambiarC(c.id, "cliente", e.target.value)}
                        />
                      </td>
                      <td>{esta ? "Ya está" : "Nuevo"}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}

      {sinTractor.length > 0 && (
        <p className="tk-meta">
          Sin tractor todavía:{" "}
          {sinTractor
            .map((s) => [s.furgon, s.chofer, s.cliente, notaDeTrafico(s.nota)].filter(Boolean).join(" · "))
            .join(" / ")}
        </p>
      )}

      {sinFurgon.length > 0 && (
        <p className="tk-meta">
          Sin furgón todavía:{" "}
          {sinFurgon.map((s) => [s.tractor, s.chofer, s.cliente].filter(Boolean).join(" · ")).join(" / ")}
        </p>
      )}

      {sinHojaC && (
        <div className="panel-aviso suave">
          <p>
            Esa planilla de furgones no tiene una hoja SALIDAS con las columnas F/C (o D/C) y
            FURGON (o FR) en el encabezado. Agregáselas y probá de nuevo.
          </p>
        </div>
      )}

      {comunes.length > 0 && vista?.furgonesId && !sinHojaC && (
        <p>
          {nuevosC.length > 0 ? (
            <>
              {elegidosC.length > 0 ? (
                <button
                  type="button"
                  className="tk-boton tk-boton--fuerte"
                  disabled={!!ocupado}
                  onClick={agregarFurgones}
                >
                  {ocupado === "agregarFurgones"
                    ? "Guardando…"
                    : `Agregar ${plural(elegidosC.length, "furgón", "furgones")} a SALIDAS`}
                </button>
              ) : (
                "Ningún furgón tildado."
              )}
              {yaEstanC.size > 0 && (
                <span className="tk-meta">
                  {" "}{yaEstanC.size} ya {yaEstanC.size === 1 ? "estaba" : "estaban"}
                </span>
              )}
            </>
          ) : (
            !avisoC && (comunes.length === 1
              ? "Ya estaba en la planilla."
              : `Los ${comunes.length} ya estaban en la planilla.`)
          )}
        </p>
      )}

      {avisoC && (
        <div className="panel-aviso suave">
          <p>{avisoC}</p>
        </div>
      )}
    </section>
  );
}
